import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuestionnaire } from '../../lib/QuestionnaireContext';
import { useAuth } from '../../lib/AuthContext';
import { deleteQuestionnaireDoc } from '../../lib/firebase';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/Card';
import { Checkbox } from '../ui/Checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup';
import PageStructureBuilder from './PageStructureBuilder';
import WebsiteReferenceBuilder, { WebsiteReference } from './WebsiteReferenceBuilder';
import RangeSlider from '../ui/RangeSlider';
import { v4 as uuidv4 } from 'uuid';
import { convertTemplateToSteps, evaluateConditionalRules } from '../../lib/templateUtils';

// Define page structure interfaces
interface PageSection {
  id: string;
  title: string;
  description: string;
}

interface Page {
  id: string;
  title: string;
  description: string;
  sections: PageSection[];
}

// Add blog and events fields to the FormData interface
interface FormData {
  // Contact Information
  name: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  
  // Current Website Information
  currentWebsiteLikesDislikes: string;
  currentWebsiteRedesignReason: string;
  
  // Project Information
  projectType: string;
  projectDescription: string;
  projectGoals: string;
  targetAudience: string;
  
  // Timeline & Budget
  timeline: string;
  budget: string;
  launchDate: string;
  
  // Page Structure & Sitemap
  existingSitemap: string;
  brandDescription: string;
  competitorWebsites: WebsiteReference[]; // Changed to array of WebsiteReference
  inspirationalWebsites: WebsiteReference[]; // Changed to array of WebsiteReference
  dislikedWebsites: WebsiteReference[]; // New field for websites the user dislikes
  sitemapFile: string; // Base64 encoded file or file URL
  pageStructure: Page[]; // New field for page structure
  requiredFeaturesFunctionalities: string; // Required features and functionalities
  
  // Features & Functionality
  requiredFeatures: string[];
  contentManagement: string;
  ecommerce: string;
  additionalNotes: string;
  
  // Project Categories
  projectCategories: string[];
  
  // Blog and Events flags
  needsBlog: string;
  needsEvents: string;
  advancedADACompliance: string; // ADA compliance requirements
  
  // E-commerce specific fields
  ecommerceProducts: string;
  ecommercePaymentGateways: string[];
  ecommerceInventory: string;
  ecommerceShipping: string[];
  ecommerceTax: boolean | string;
  ecommerceSubscriptions: boolean | string;
  ecommerceAdditionalNotes: string;
  ecommerceProductCount: string;
  ecommerceCustomization: string;
  ecommerceDiscounts: string;
  ecommerceWholesale: string;
  ecommerceStoreLocator: string;
  ecommerceComments: string;
  
  // Restaurant specific fields
  restaurantReservations: string;
  restaurantReservationSystem: string;
  restaurantMenuPresentation: string;
  restaurantOtherFeatures: string;
  
  // Real Estate specific fields
  realEstatePropertyManagement: string;
  realEstatePropertyManagementDetails: string;
  realEstateDisplayUpdates: string;
  realEstateLeadsToCRM: string;
  realEstateDisplayMap: string;
  realEstateMapType: string;
  
  // Hotels/Hospitality specific fields
  hospitalityBookingSystem: string;
  hospitalityBookingSystemDetails: string;
  hospitalityDisplayMap: string;
  hospitalityMapType: string;
  
  // Blog specific fields
  blogUtilization: string;
  blogContentType: string;
  
  // Events specific fields
  eventsSellTickets: string;
  eventsThirdParty: string;
  eventsThirdPartyName: string;
  eventsThirdPartyOther: string;
  eventsRequireRSVP: string;
  
  // Content Management fields - expanded
  contentManagementNeeds: string;
  existingContent: string;
  initialContentUpload: string;
  contentUpdateFrequency: string;
  contentUpdateManagement: string;
  
  [key: string]: any;
}

// Define field types for better type checking
interface BaseField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  helpText?: string;
  conditional?: (formData: FormData) => boolean;
}

interface TextField extends BaseField {
  type: 'text' | 'email' | 'tel' | 'url' | 'date';
  placeholder: string;
}

interface TextareaField extends BaseField {
  type: 'textarea';
  placeholder: string;
}

interface SelectField extends BaseField {
  type: 'select';
  options: { value: string; label: string }[];
}

interface RadioField extends BaseField {
  type: 'radio';
  options: { value: string; label: string }[];
}

interface CheckboxField extends BaseField {
  type: 'checkbox';
  options: { value: string; label: string }[];
}

interface FileField extends BaseField {
  type: 'file';
  accept?: string;
}

interface CustomField extends BaseField {
  type: 'custom';
  component: string;
}

interface PageStructureField extends BaseField {
  type: 'pageStructure';
}

interface WebsiteReferencesField extends BaseField {
  type: 'websiteReferences';
}

interface RepeaterField extends BaseField {
  type: 'repeater';
}

interface RangeField extends BaseField {
  type: 'range';
  min?: number;
  max?: number;
}

type Field = TextField | TextareaField | SelectField | RadioField | CheckboxField | FileField | CustomField | PageStructureField | WebsiteReferencesField | RepeaterField | RangeField;

interface Step {
  title: string;
  description: string;
  fields: Field[];
  conditional?: (formData: FormData) => boolean;
}

// Define steps
const allSteps: Step[] = [
  // Contact Information - Split into two parts
  {
    title: 'Contact Information - Basic',
    description: 'Let\'s start with your basic contact details',
    fields: [
      {
        name: 'name',
        label: 'Your Name',
        type: 'text',
        placeholder: 'Enter your full name',
        required: true,
      },
      {
        name: 'email',
        label: 'Email Address',
        type: 'email',
        placeholder: 'Enter your email address',
        required: true,
      },
      {
        name: 'phone',
        label: 'Phone Number',
        type: 'tel',
        placeholder: 'Enter your phone number',
        required: true,
      },
    ],
  },
  {
    title: 'Contact Information - Company',
    description: 'Tell us about your company',
    fields: [
      {
        name: 'company',
        label: 'Company Name',
        type: 'text',
        placeholder: 'Enter your company name',
        required: true,
      },
      {
        name: 'website',
        label: 'Current Website',
        type: 'url',
        placeholder: 'https://example.com',
        required: false,
        helpText: 'If you have an existing website, please provide the URL'
      },
    ],
  },
  
  // Current Website Information (conditional)
  {
    title: 'Current Website Information',
    description: 'Tell us about your current website',
    fields: [
      {
        name: 'currentWebsiteLikesDislikes',
        label: 'What are the likes and dislikes?',
        type: 'textarea',
        placeholder: 'Please describe what you like and dislike about your current website',
        required: false,
      },
      {
        name: 'currentWebsiteRedesignReason',
        label: 'What are the reasons for redesigning or developing a new one?',
        type: 'textarea',
        placeholder: 'Please describe your reasons for wanting a new website',
        required: false,
      },
    ],
    conditional: (formData: FormData) => {
      return !!formData.website && formData.website.trim() !== '';
    }
  },
  
  // Project Categories
  {
    title: 'Project Categories',
    description: 'What type of project are you interested in?',
    fields: [
      {
        name: 'projectCategories',
        label: 'Project Categories',
        type: 'checkbox',
        options: [
          { value: 'ecommerce', label: 'Ecommerce' },
          { value: 'portfolio', label: 'Portfolio' },
          { value: 'restaurant', label: 'Restaurant' },
          { value: 'realEstate', label: 'Real Estate' },
          { value: 'hospitality', label: 'Hotels/Hospitality' },
          { value: 'corporate', label: 'Corporate/Business/Non-profit' },
          { value: 'other', label: 'Other' }
        ],
        required: true,
        helpText: 'Select all that apply to your project'
      },
    ]
  },
  
  // New step for Blog and Events
  {
    title: 'Blog and Events',
    description: 'Does your website need a blog or events section?',
    fields: [
      {
        name: 'needsBlog',
        label: 'Does your website need a blog?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'needsEvents',
        label: 'Does your website need an events section?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      }
    ]
  },
  
  // New step for ADA Compliance
  {
    title: 'ADA Compliance',
    description: 'Does your website need advanced ADA compliance standards?',
    fields: [
      {
        name: 'advancedADACompliance',
        label: 'SDCO develops all websites in accordance with 2.1 Single A ADA Compliance standards. However, there are multiple levels of ADA compliance beyond this baseline. Does your website need to adhere to any of the more advanced ADA standards?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
        helpText: 'ADA is defined as the Americans with Disabilities Act and you can read more about it here: <a href="https://www.ada.gov/" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">ADA Guidelines</a>. If your site requires a higher level of ADA compliance SDCO may recommend you work with a website development agency specializing in this. In all cases, SDCO recommends you consult with an attorney to determine your site\'s needs.'
      }
    ]
  },
  
  {
    title: 'Project Description',
    description: 'Tell us more about your project',
    fields: [
      {
        name: 'projectDescription',
        label: 'Project Description',
        type: 'textarea',
        placeholder: 'Please describe your project in detail',
        required: true,
        helpText: 'What are you looking to build? What problem does it solve?'
      },
      {
        name: 'projectGoals',
        label: 'Project Goals',
        type: 'textarea',
        placeholder: 'What are you trying to achieve with this project?',
        required: true,
        helpText: 'What specific outcomes are you hoping for?'
      },
    ],
  },
  {
    title: 'Target Audience',
    description: 'Who is your project aimed at?',
    fields: [
      {
        name: 'targetAudience',
        label: 'Target Audience',
        type: 'textarea',
        placeholder: 'Who is your target audience?',
        required: true,
        helpText: 'Describe the demographic, interests, and behaviors of your ideal users'
      },
    ],
  },
  
  // Timeline & Budget - Split into two parts
  {
    title: 'Project Timeline',
    description: 'Tell us about your project timeline',
    fields: [
      {
        name: 'timeline',
        label: 'Project Timeline',
        type: 'select',
        options: [
          { value: '', label: 'Select timeline' },
          { value: 'urgent', label: 'Urgent (< 1 month)' },
          { value: 'short', label: 'Short (1-3 months)' },
          { value: 'medium', label: 'Medium (3-6 months)' },
          { value: 'long', label: 'Long (6+ months)' },
          { value: 'flexible', label: 'Flexible' }
        ],
        required: true,
      },
      {
        name: 'launchDate',
        label: 'Desired Launch Date',
        type: 'date',
        placeholder: 'MM/DD/YYYY',
        required: false,
        helpText: 'When would you like to launch your project?'
      },
    ],
  },
  {
    title: 'Budget Information',
    description: 'Tell us about your budget expectations',
    fields: [
      {
        name: 'budget',
        label: 'Budget Range',
        type: 'range',
        min: 0,
        max: 150000,
        required: true,
      },
    ],
  },
  
  // Site Structure
  {
    title: 'Site Structure',
    description: 'Tell us about your website structure',
    fields: [
      {
        name: 'existingSitemap',
        label: 'Do you have an existing sitemap?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'inProgress', label: 'In progress' }
        ],
        required: true,
      },
      {
        name: 'sitemapFile',
        label: 'Sitemap File',
        type: 'file',
        accept: '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg',
        required: false,
        helpText: 'Please upload your sitemap file (PDF, Word, Excel, or image format)',
        conditional: (formData: FormData) => {
          return formData.existingSitemap === 'yes' || formData.existingSitemap === 'inProgress';
        }
      },
      {
        name: 'requiredFeaturesFunctionalities',
        label: 'Are there specific features or functionalities you must have?',
        type: 'textarea',
        placeholder: 'e.g., search, login, filtering, etc.',
        required: false,
        helpText: 'List any specific features or functionalities that are essential for your website'
      },
    ],
  },
  {
    title: 'Page Requirements',
    description: 'Tell us about your page requirements',
    fields: [
      {
        name: 'pageStructure',
        label: 'Page Structure',
        type: 'pageStructure',
        required: true,
        helpText: 'Define the pages, subpages, and sections for your website'
      }
    ],
  },
  {
    title: 'Website References',
    description: 'Share websites that inspire you, your competitors, and websites you dislike',
    fields: [
      {
        name: 'competitorWebsites',
        label: 'Competitor Websites',
        type: 'websiteReferences',
        required: false,
        helpText: 'Add websites of your direct competitors'
      },
      {
        name: 'inspirationalWebsites',
        label: 'Inspirational Websites',
        type: 'websiteReferences',
        required: false,
        helpText: 'Add websites whose design or functionality you admire'
      },
      {
        name: 'dislikedWebsites',
        label: 'Websites You Dislike',
        type: 'websiteReferences',
        required: false,
        helpText: 'Add websites whose design or functionality you want to avoid'
      }
    ],
  },
  
  // Features & Functionality
  
  // Ecommerce specific section (conditional)
  {
    title: 'Ecommerce Features',
    description: 'Tell us more about your ecommerce requirements',
    fields: [
      {
        name: 'ecommerceProductCount',
        label: 'Estimate how many products are you going to sell',
        type: 'radio',
        options: [
          { value: 'less-than-5', label: 'Less than 5' },
          { value: 'less-than-20', label: 'Less than 20' },
          { value: 'less-than-50', label: 'Less than 50' },
          { value: 'more-than-50', label: 'More than 50' }
        ],
        required: true,
      },
      {
        name: 'ecommerceCustomization',
        label: 'Do your products require any customization?',
        type: 'textarea',
        placeholder: 'If so, briefly describe the customization requirements',
        required: false,
      },
      {
        name: 'ecommerceDiscounts',
        label: 'Do you plan on offering any subscriptions or bundling discounts?',
        type: 'radio',
        options: [
          { value: 'subscriptions', label: 'Subscriptions' },
          { value: 'bundling', label: 'Bundling Discounts' },
          { value: 'both', label: 'Both' },
          { value: 'none', label: 'None' }
        ],
        required: true,
      },
      {
        name: 'ecommerceWholesale',
        label: 'Do you plan on offering any wholesale or trade accounts?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'ecommerceStoreLocator',
        label: 'Do you sell your products in stores and will there need to be a store locator?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'ecommerceComments',
        label: 'Comments',
        type: 'textarea',
        placeholder: 'Any other notes to mention about your e-commerce functionality?',
        required: false,
      },
    ],
    conditional: (formData: FormData) => {
      return formData.projectCategories.includes('ecommerce');
    }
  },
  
  // Restaurant specific section (conditional)
  {
    title: 'Restaurant Features',
    description: 'Tell us more about your restaurant website requirements',
    fields: [
      {
        name: 'restaurantReservations',
        label: 'Do you require any reservations?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'restaurantReservationSystem',
        label: 'If yes, do you have a reservation system you plan to use?',
        type: 'radio',
        options: [
          { value: 'resy', label: 'Resy' },
          { value: 'opentable', label: 'Open Table' },
          { value: 'other', label: 'Other/TBD' }
        ],
        required: false,
        conditional: (formData: FormData) => {
          return formData.restaurantReservations === 'yes';
        }
      },
      {
        name: 'restaurantMenuPresentation',
        label: 'How would you like your menus to be presented?',
        type: 'radio',
        options: [
          { value: 'pdf', label: 'Uploadable PDFs' },
          { value: 'cms', label: 'Editable in the CMS' }
        ],
        required: true,
      },
      {
        name: 'restaurantOtherFeatures',
        label: 'Are there any other features you need?',
        type: 'textarea',
        placeholder: 'Events, Preorders, Gift Cards, Merch Shop, etc.',
        required: false,
      },
    ],
    conditional: (formData: FormData) => {
      return formData.projectCategories.includes('restaurant');
    }
  },
  
  // Real Estate specific section (conditional)
  {
    title: 'Real Estate Features',
    description: 'Tell us more about your real estate website requirements',
    fields: [
      {
        name: 'realEstatePropertyManagement',
        label: 'Do you have a Property Management Tool or MLS system you need to integrate with the website?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'realEstatePropertyManagementDetails',
        label: 'If yes, please provide details',
        type: 'textarea',
        placeholder: 'PropertyBase, etc.',
        required: false,
        conditional: (formData: FormData) => {
          return formData.realEstatePropertyManagement === 'yes';
        }
      },
      {
        name: 'realEstateDisplayUpdates',
        label: 'Do you plan on using the website to display up to date information?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
        helpText: 'Such as availability, pricing, under contract, etc.'
      },
      {
        name: 'realEstateLeadsToCRM',
        label: 'Do you want to have contact leads feed into CRM with information about the property they are interested in?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'realEstateDisplayMap',
        label: 'Do you want to display a map highlighting the property and surrounding area?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'realEstateMapType',
        label: 'If yes, what type of map?',
        type: 'radio',
        options: [
          { value: 'google', label: 'Google Map' },
          { value: 'illustrated', label: 'Illustrated Map' },
          { value: 'custom', label: 'Custom Interactive Map' }
        ],
        required: false,
        conditional: (formData: FormData) => {
          return formData.realEstateDisplayMap === 'yes';
        }
      },
    ],
    conditional: (formData: FormData) => {
      return formData.projectCategories.includes('realEstate');
    }
  },
  
  // Hotels/Hospitality specific section (conditional)
  {
    title: 'Hotels/Hospitality Features',
    description: 'Tell us more about your hotel or hospitality website requirements',
    fields: [
      {
        name: 'hospitalityBookingSystem',
        label: 'Do you have a Hotel booking system or Central Reservation System (CRS) you are currently using?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'recommend', label: 'I would like SDCO to recommend one' }
        ],
        required: true,
      },
      {
        name: 'hospitalityBookingSystemDetails',
        label: 'If yes, please provide the name of your CRS',
        type: 'textarea',
        placeholder: 'Synxis, Mews, etc.',
        required: false,
        conditional: (formData: FormData) => {
          return formData.hospitalityBookingSystem === 'yes';
        }
      },
      {
        name: 'hospitalityDisplayMap',
        label: 'Do you want to display a map highlighting the property and surrounding area?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'hospitalityMapType',
        label: 'If yes, what type of map?',
        type: 'radio',
        options: [
          { value: 'google', label: 'Google Map' },
          { value: 'illustrated', label: 'Illustrated Map' },
          { value: 'custom', label: 'Custom Interactive Map' }
        ],
        required: false,
        conditional: (formData: FormData) => {
          return formData.hospitalityDisplayMap === 'yes';
        }
      },
    ],
    conditional: (formData: FormData) => {
      return formData.projectCategories.includes('hospitality');
    }
  },
  
  // Blog specific section (conditional)
  {
    title: 'Blog Features',
    description: 'Tell us more about your blog requirements',
    fields: [
      {
        name: 'blogUtilization',
        label: 'How do you plan on utilizing your blog?',
        type: 'textarea',
        placeholder: 'Describe how you plan to use your blog (e.g., sharing company news, industry insights, etc.)',
        required: false,
      },
      {
        name: 'blogContentType',
        label: 'What type of content do you plan on featuring in your blog?',
        type: 'textarea',
        placeholder: 'Describe the types of content you plan to feature (e.g., articles, videos, podcasts, image galleries, etc.)',
        required: false,
      }
    ],
    conditional: (formData: FormData) => {
      return formData.needsBlog === 'yes';
    }
  },
  
  // Events specific section (conditional)
  {
    title: 'Events Features',
    description: 'Tell us more about your events requirements',
    fields: [
      {
        name: 'eventsSellTickets',
        label: 'Do you plan on selling tickets?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        required: true,
      },
      {
        name: 'eventsThirdParty',
        label: 'Do you plan on using a third party to fulfill these tickets?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No, I want to sell tickets directly to users without a third party' }
        ],
        required: true,
        conditional: (formData: FormData) => {
          return formData.eventsSellTickets === 'yes';
        }
      },
      {
        name: 'eventsThirdPartyName',
        label: 'Which third party service do you plan to use?',
        type: 'select',
        options: [
          { value: '', label: 'Select a service' },
          { value: 'eventbrite', label: 'Eventbrite' },
          { value: 'ticketmaster', label: 'Ticketmaster' },
          { value: 'meetup', label: 'Meetup' },
          { value: 'other', label: 'Other' }
        ],
        required: true,
        conditional: (formData: FormData) => {
          return formData.eventsSellTickets === 'yes' && formData.eventsThirdParty === 'yes';
        }
      },
      {
        name: 'eventsThirdPartyOther',
        label: 'Please specify the third party service',
        type: 'text',
        placeholder: 'Enter the name of the third party service',
        required: true,
        conditional: (formData: FormData) => {
          return formData.eventsSellTickets === 'yes' && 
                 formData.eventsThirdParty === 'yes' && 
                 formData.eventsThirdPartyName === 'other';
        }
      },
      {
        name: 'eventsRequireRSVP',
        label: 'Do you plan on requiring RSVP/registration to events?',
        type: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No, I just want to display upcoming events with details' }
        ],
        required: true,
        conditional: (formData: FormData) => {
          return formData.eventsSellTickets === 'no';
        }
      }
    ],
    conditional: (formData: FormData) => {
      return formData.needsEvents === 'yes';
    }
  },
  
  // Content Management - Existing Content
  {
    title: 'Existing Content',
    description: 'Tell us about any existing content you have',
    fields: [
      {
        name: 'existingContent',
        label: 'Do you have existing content that needs to be revised or reused?',
        type: 'radio',
        options: [
          { value: 'reuse', label: 'Yes, I would like to reuse existing content.' },
          { value: 'revise', label: 'Yes, I would like SDCO to revise existing content.' },
          { value: 'none', label: 'No, I do not have any existing content.' }
        ],
        required: true,
      },
    ],
  },
  
  // Content Management - Initial Content Upload
  {
    title: 'Initial Content Upload',
    description: 'Tell us about your initial content upload needs',
    fields: [
      {
        name: 'initialContentUpload',
        label: 'Prior to launch, how much content do you want to be uploaded?',
        type: 'radio',
        options: [
          { value: 'default', label: 'Homepage, interior page, and example product or post (e.g: portfolio, article, etc.)' },
          { value: 'top-level', label: 'I would like SDCO to upload all top level pages (e.g: Home, About, Services, etc.)' },
          { value: 'all', label: 'I would like all content to be uploaded by SDCO' }
        ],
        required: true,
      },
    ],
  },
  
  // Content Management - Content Updates
  {
    title: 'Content Updates',
    description: 'Tell us about your content update needs after launch',
    fields: [
      {
        name: 'contentUpdateFrequency',
        label: 'After Launch, how often do you foresee content changing?',
        type: 'radio',
        options: [
          { value: 'frequent', label: 'Frequent (multiple times per week)' },
          { value: 'sometimes', label: 'Sometimes (a couple times per month)' },
          { value: 'rarely', label: 'Rarely (a couple times per year)' }
        ],
        required: true,
      },
      {
        name: 'contentUpdateManagement',
        label: 'Who would you like to manage these content updates?',
        type: 'radio',
        options: [
          { value: 'self', label: 'I am comfortable making these updates' },
          { value: 'team', label: 'I have a team to support with these updates' },
          { value: 'sdco', label: 'I would like SDCO to make these updates for me' }
        ],
        required: true,
      },
    ],
  },
];

// Function to get active steps based on form data and template
const getActiveSteps = (formData: FormData, template: any): Step[] => {
  // // console.log('Getting active steps from template:', template?.name);
  
  // If no template is loaded, fall back to hardcoded steps
  if (!template) {
    // // console.log('No template loaded, using hardcoded steps');
    const filteredSteps = allSteps.filter(step => {
      if (step.conditional) {
        const shouldInclude = step.conditional(formData);
        // // console.log(`Step "${step.title}" conditional evaluated to: ${shouldInclude}`);
        return shouldInclude;
      }
      return true;
    });
    return filteredSteps;
  }
  
  // Convert template to steps and apply conditional logic
  const templateSteps = convertTemplateToSteps(template, formData);
  // // console.log('Converted template to steps with conditional logic:', templateSteps.map(s => s.title));
  
  return templateSteps;
};

// Helper function to get current section information for pagination
// This logic must match exactly with QuestionnaireLayout.tsx sidebar generation
const getCurrentSectionInfo = (currentStep: number, activeSteps: Step[], template: any, formData: any) => {
  if (!template || !template.sections) {
    return { currentSection: 1, totalSections: 1, subsectionProgress: 0 };
  }

  // Sort sections by order (same as sidebar)
  const sortedSections = [...template.sections].sort((a, b) => a.order - b.order);
  
  // Filter visible sections (same as sidebar logic)
  const visibleSections: any[] = [];
  let stepCounter = 0;
  let currentSectionIndex = 0;
  let subsectionIndex = 0;
  let totalSubsections = 1;
  
  sortedSections.forEach((section, sectionIdx) => {
    // Check if this section should be visible based on conditional rules (same as sidebar)
    const isSectionVisible = !section.conditionalRules || section.conditionalRules.length === 0 || 
      evaluateConditionalRules(section.conditionalRules, formData);
    
    if (!isSectionVisible) {
      return; // Skip this section
    }
    
    if (section.subsections && section.subsections.length > 0) {
      // Filter visible subsections (same as sidebar)
      const visibleSubsections = section.subsections.filter((subsection: any) => {
        return !subsection.conditionalRules || subsection.conditionalRules.length === 0 ||
          evaluateConditionalRules(subsection.conditionalRules, formData);
      });
      
      if (visibleSubsections.length > 0) {
        visibleSections.push(section); // This section will be visible in sidebar
        
        if (visibleSubsections.length === 1) {
          // Single subsection treated as one step (same as sidebar)
          if (stepCounter === currentStep) {
            currentSectionIndex = visibleSections.length - 1;
            subsectionIndex = 0;
            totalSubsections = 1;
          }
          stepCounter++;
        } else {
          // Multiple subsections (same as sidebar)
          for (let subIdx = 0; subIdx < visibleSubsections.length; subIdx++) {
            if (stepCounter === currentStep) {
              currentSectionIndex = visibleSections.length - 1;
              subsectionIndex = subIdx;
              totalSubsections = visibleSubsections.length;
            }
            stepCounter++;
          }
        }
      }
    }
  });
  
  return {
    currentSection: currentSectionIndex + 1,
    totalSections: visibleSections.length, // Only count visible sections
    subsectionProgress: totalSubsections > 1 ? (subsectionIndex + 1) / totalSubsections : 1
  };
};

const QuestionnaireForm: React.FC = () => {
  // // console.log('QuestionnaireForm component rendering');
  
  const { currentUser } = useAuth();
  const { 
    currentQuestionnaire, 
    loadQuestionnaire, 
    updateQuestionnaireData, 
    submitQuestionnaire, 
    loading,
    loadUserQuestionnaires,
    activeTemplate,
    loadActiveTemplate
  } = useQuestionnaire();
  
  // // console.log('Current state:', { 
  //   currentQuestionnaire, 
  //   loading
  // });
  
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    // Contact Information
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    
    // Current Website Information
    currentWebsiteLikesDislikes: '',
    currentWebsiteRedesignReason: '',
    
    // Project Information
    projectType: 'website', // Default to website
    projectDescription: '',
    projectGoals: '',
    targetAudience: '',
    
    // Timeline & Budget
    timeline: '',
    budget: '',
    launchDate: '',
    
    // Page Structure & Sitemap
    existingSitemap: '',
    brandDescription: '',
    competitorWebsites: [], // Initialize as empty array of WebsiteReference
    inspirationalWebsites: [], // Initialize as empty array of WebsiteReference
    dislikedWebsites: [], // Initialize as empty array of WebsiteReference
    sitemapFile: '', // Base64 encoded file or file URL
    pageStructure: [], // Initialize as empty array
    requiredFeaturesFunctionalities: '', // Required features and functionalities
    
    // Features & Functionality
    requiredFeatures: [],
    contentManagement: '',
    ecommerce: '',
    additionalNotes: '',
    
    // Project Categories
    projectCategories: [],
    
    // Blog and Events flags
    needsBlog: '',
    needsEvents: '',
    advancedADACompliance: 'no', // ADA compliance requirements - default to No
    
    // E-commerce specific fields
    ecommerceProducts: '',
    ecommercePaymentGateways: [],
    ecommerceInventory: '',
    ecommerceShipping: [],
    ecommerceTax: false,
    ecommerceSubscriptions: false,
    ecommerceAdditionalNotes: '',
    ecommerceProductCount: '',
    ecommerceCustomization: '',
    ecommerceDiscounts: '',
    ecommerceWholesale: '',
    ecommerceStoreLocator: '',
    ecommerceComments: '',
    
    // Restaurant specific fields
    restaurantReservations: '',
    restaurantReservationSystem: '',
    restaurantMenuPresentation: '',
    restaurantOtherFeatures: '',
    
    // Real Estate specific fields
    realEstatePropertyManagement: '',
    realEstatePropertyManagementDetails: '',
    realEstateDisplayUpdates: '',
    realEstateLeadsToCRM: '',
    realEstateDisplayMap: '',
    realEstateMapType: '',
    
    // Hotels/Hospitality specific fields
    hospitalityBookingSystem: '',
    hospitalityBookingSystemDetails: '',
    hospitalityDisplayMap: '',
    hospitalityMapType: '',
    
    // Blog specific fields
    blogUtilization: '',
    blogContentType: '',
    
    // Events specific fields
    eventsSellTickets: '',
    eventsThirdParty: '',
    eventsThirdPartyName: '',
    eventsThirdPartyOther: '',
    eventsRequireRSVP: '',
    
    // Content Management fields - expanded
    contentManagementNeeds: '',
    existingContent: '',
    initialContentUpload: 'default',
    contentUpdateFrequency: '',
    contentUpdateManagement: '',
  });
  const [savingStep, setSavingStep] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  
  // Parse the step from URL query parameters
  const getStepFromQuery = (): number => {
    const searchParams = new URLSearchParams(location.search);
    const stepParam = searchParams.get('step');
    return stepParam ? parseInt(stepParam, 10) : (currentQuestionnaire?.currentStep || 0);
  };
  
  // Get projectType from URL query parameters
  const getProjectTypeFromQuery = (): string => {
    const searchParams = new URLSearchParams(location.search);
    return 'website'; // Always return 'website' regardless of the query parameter
  };
  
  // // console.log('Component params and state:', { id, currentStep, hasAttemptedLoad, loadError });

  // Load questionnaire data when component mounts or ID changes
  useEffect(() => {
    // // console.log('Load questionnaire effect running with ID:', id);
    
    const loadQuestionnaireData = async () => {
      if (!id) {
        // // console.log('No ID provided, redirecting to questionnaire home');
        navigate('/questionnaire');
        return;
      }
      
      // Prevent multiple load attempts
      if (hasAttemptedLoad) {
        // // console.log('Already attempted to load, skipping');
        return;
      }
      
      setHasAttemptedLoad(true);
      
      try {
        // console.log('Attempting to load questionnaire with ID:', id);
        setLoadError(null);
        await loadQuestionnaire(id);
        // // console.log('Successfully loaded questionnaire');
        
        // Check for projectType in URL and update if needed
        const projectTypeFromUrl = getProjectTypeFromQuery();
        if (projectTypeFromUrl && currentQuestionnaire) {
          // // console.log('Found projectType in URL:', projectTypeFromUrl);
          // Only update if the questionnaire is loaded and projectType is different
          if (currentQuestionnaire.formData.projectType !== projectTypeFromUrl) {
            await updateQuestionnaireData({ projectType: projectTypeFromUrl }, 0);
          }
          
          // Remove the projectType parameter from URL to avoid applying it again on refresh
          const params = new URLSearchParams(location.search);
          params.delete('projectType');
          
          // Keep the step parameter if it exists
          const stepParam = params.get('step');
          const newUrl = stepParam 
            ? `/questionnaire/form/${id}?step=${stepParam}`
            : `/questionnaire/form/${id}`;
            
          navigate(newUrl, { replace: true });
        }
      } catch (error) {
        console.error('Error loading questionnaire:', error);
        setLoadError('Failed to load questionnaire. It may have been deleted or you may not have permission to access it.');
        // Redirect to home page after a short delay when questionnaire can't be loaded
        setTimeout(() => {
          navigate('/questionnaire');
        }, 2000);
      }
    };

    loadQuestionnaireData();
  }, [id, loadQuestionnaire, navigate, hasAttemptedLoad, updateQuestionnaireData, currentQuestionnaire]);

  // Update local state when currentQuestionnaire changes or when URL step param changes
  useEffect(() => {
    // console.log('Current questionnaire changed or URL step param changed');
    if (currentQuestionnaire) {
      // console.log('Current questionnaire form data:', currentQuestionnaire.formData);
      // console.log('Project categories from loaded questionnaire:', currentQuestionnaire.formData.projectCategories);
      // console.log('Real Estate fields from loaded questionnaire:', {
      //   propertyManagement: currentQuestionnaire.formData.realEstatePropertyManagement,
      //   displayUpdates: currentQuestionnaire.formData.realEstateDisplayUpdates,
      //   leadsToCRM: currentQuestionnaire.formData.realEstateLeadsToCRM,
      //   displayMap: currentQuestionnaire.formData.realEstateDisplayMap,
      //   mapType: currentQuestionnaire.formData.realEstateMapType
      // });
      
      // Make sure we have all required fields in the form data
      const updatedFormData = {
        // Contact Information
        name: currentQuestionnaire.formData.name || '',
        email: currentQuestionnaire.formData.email || '',
        phone: currentQuestionnaire.formData.phone || '',
        company: currentQuestionnaire.formData.company || '',
        website: currentQuestionnaire.formData.website || '',
        
        // Current Website Information
        currentWebsiteLikesDislikes: currentQuestionnaire.formData.currentWebsiteLikesDislikes || '',
        currentWebsiteRedesignReason: currentQuestionnaire.formData.currentWebsiteRedesignReason || '',
        
        // Project Information
        projectType: currentQuestionnaire.formData.projectType || '',
        projectDescription: currentQuestionnaire.formData.projectDescription || '',
        projectGoals: currentQuestionnaire.formData.projectGoals || '',
        targetAudience: currentQuestionnaire.formData.targetAudience || '',
        
        // Timeline & Budget
        timeline: currentQuestionnaire.formData.timeline || '',
        budget: currentQuestionnaire.formData.budget || '',
        launchDate: currentQuestionnaire.formData.launchDate || '',
        
        // Page Structure & Sitemap
        existingSitemap: currentQuestionnaire.formData.existingSitemap || '',
        brandDescription: currentQuestionnaire.formData.brandDescription || '',
        competitorWebsites: currentQuestionnaire.formData.competitorWebsites || [], // Handle as array
        inspirationalWebsites: currentQuestionnaire.formData.inspirationalWebsites || [], // Handle as array
        dislikedWebsites: currentQuestionnaire.formData.dislikedWebsites || [], // Handle as array
        sitemapFile: currentQuestionnaire.formData.sitemapFile || '', // Base64 encoded file or file URL
        pageStructure: currentQuestionnaire.formData.pageStructure || [], // Update pageStructure
        requiredFeaturesFunctionalities: currentQuestionnaire.formData.requiredFeaturesFunctionalities || '', // Required features and functionalities
        
        // Features & Functionality
        requiredFeatures: currentQuestionnaire.formData.requiredFeatures || [],
        contentManagement: currentQuestionnaire.formData.contentManagement || '',
        ecommerce: currentQuestionnaire.formData.ecommerce || '',
        additionalNotes: currentQuestionnaire.formData.additionalNotes || '',
        
        // Project Categories
        projectCategories: currentQuestionnaire.formData.projectCategories || [],
        
        // Blog and Events flags
        needsBlog: currentQuestionnaire.formData.needsBlog || '',
        needsEvents: currentQuestionnaire.formData.needsEvents || '',
        advancedADACompliance: currentQuestionnaire.formData.advancedADACompliance || 'no', // ADA compliance requirements - default to No
        
        // E-commerce specific fields
        ecommerceProducts: currentQuestionnaire.formData.ecommerceProducts || '',
        ecommercePaymentGateways: currentQuestionnaire.formData.ecommercePaymentGateways || [],
        ecommerceInventory: currentQuestionnaire.formData.ecommerceInventory || '',
        ecommerceShipping: currentQuestionnaire.formData.ecommerceShipping || [],
        ecommerceTax: typeof currentQuestionnaire.formData.ecommerceTax === 'boolean' 
          ? currentQuestionnaire.formData.ecommerceTax 
          : currentQuestionnaire.formData.ecommerceTax === 'true' 
            ? true 
            : currentQuestionnaire.formData.ecommerceTax === 'false' 
              ? false 
              : false,
        ecommerceSubscriptions: typeof currentQuestionnaire.formData.ecommerceSubscriptions === 'boolean' 
          ? currentQuestionnaire.formData.ecommerceSubscriptions 
          : currentQuestionnaire.formData.ecommerceSubscriptions === 'true' 
            ? true 
            : currentQuestionnaire.formData.ecommerceSubscriptions === 'false' 
              ? false 
              : false,
        ecommerceAdditionalNotes: currentQuestionnaire.formData.ecommerceAdditionalNotes || '',
        ecommerceProductCount: currentQuestionnaire.formData.ecommerceProductCount || '',
        ecommerceCustomization: currentQuestionnaire.formData.ecommerceCustomization || '',
        ecommerceDiscounts: currentQuestionnaire.formData.ecommerceDiscounts || '',
        ecommerceWholesale: currentQuestionnaire.formData.ecommerceWholesale || '',
        ecommerceStoreLocator: currentQuestionnaire.formData.ecommerceStoreLocator || '',
        ecommerceComments: currentQuestionnaire.formData.ecommerceComments || '',
        
        // Restaurant specific fields
        restaurantReservations: currentQuestionnaire.formData.restaurantReservations || '',
        restaurantReservationSystem: currentQuestionnaire.formData.restaurantReservationSystem || '',
        restaurantMenuPresentation: currentQuestionnaire.formData.restaurantMenuPresentation || '',
        restaurantOtherFeatures: currentQuestionnaire.formData.restaurantOtherFeatures || '',
        
        // Real Estate specific fields
        realEstatePropertyManagement: currentQuestionnaire.formData.realEstatePropertyManagement || '',
        realEstatePropertyManagementDetails: currentQuestionnaire.formData.realEstatePropertyManagementDetails || '',
        realEstateDisplayUpdates: currentQuestionnaire.formData.realEstateDisplayUpdates || '',
        realEstateLeadsToCRM: currentQuestionnaire.formData.realEstateLeadsToCRM || '',
        realEstateDisplayMap: currentQuestionnaire.formData.realEstateDisplayMap || '',
        realEstateMapType: currentQuestionnaire.formData.realEstateMapType || '',
        
        // Hotels/Hospitality specific fields
        hospitalityBookingSystem: currentQuestionnaire.formData.hospitalityBookingSystem || '',
        hospitalityBookingSystemDetails: currentQuestionnaire.formData.hospitalityBookingSystemDetails || '',
        hospitalityDisplayMap: currentQuestionnaire.formData.hospitalityDisplayMap || '',
        hospitalityMapType: currentQuestionnaire.formData.hospitalityMapType || '',
        
        // Blog specific fields
        blogUtilization: currentQuestionnaire.formData.blogUtilization || '',
        blogContentType: currentQuestionnaire.formData.blogContentType || '',
        
        // Events specific fields
        eventsSellTickets: currentQuestionnaire.formData.eventsSellTickets || '',
        eventsThirdParty: currentQuestionnaire.formData.eventsThirdParty || '',
        eventsThirdPartyName: currentQuestionnaire.formData.eventsThirdPartyName || '',
        eventsThirdPartyOther: currentQuestionnaire.formData.eventsThirdPartyOther || '',
        eventsRequireRSVP: currentQuestionnaire.formData.eventsRequireRSVP || '',
        
        // Content Management fields - expanded
        contentManagementNeeds: currentQuestionnaire.formData.contentManagementNeeds || '',
        existingContent: currentQuestionnaire.formData.existingContent || '',
        initialContentUpload: currentQuestionnaire.formData.initialContentUpload || '',
        contentUpdateFrequency: currentQuestionnaire.formData.contentUpdateFrequency || '',
        contentUpdateManagement: currentQuestionnaire.formData.contentUpdateManagement || '',
        
        // Include ALL other dynamic fields from template questions (this preserves file uploads and other dynamic fields)
        ...Object.keys(currentQuestionnaire.formData).reduce((acc, key) => {
          // Only add fields that aren't already explicitly defined above
          if (!(key in {
            name: '', email: '', phone: '', company: '', website: '',
            currentWebsiteLikesDislikes: '', currentWebsiteRedesignReason: '',
            projectType: '', projectDescription: '', projectGoals: '', targetAudience: '',
            timeline: '', budget: '', launchDate: '',
            existingSitemap: '', brandDescription: '',
            competitorWebsites: [], inspirationalWebsites: [], dislikedWebsites: [],
            sitemapFile: '', pageStructure: [], requiredFeaturesFunctionalities: '',
            requiredFeatures: [], contentManagement: '', ecommerce: '', additionalNotes: '',
            projectCategories: [], needsBlog: '', needsEvents: '', advancedADACompliance: '',
            ecommerceProducts: '', ecommercePaymentGateways: [], ecommerceInventory: '',
            ecommerceShipping: [], ecommerceTax: false, ecommerceSubscriptions: false,
            ecommerceAdditionalNotes: '', ecommerceProductCount: '', ecommerceCustomization: '',
            ecommerceDiscounts: '', ecommerceWholesale: '', ecommerceStoreLocator: '', ecommerceComments: '',
            restaurantReservations: '', restaurantReservationSystem: '', restaurantMenuPresentation: '', restaurantOtherFeatures: '',
            realEstatePropertyManagement: '', realEstatePropertyManagementDetails: '',
            realEstateDisplayUpdates: '', realEstateLeadsToCRM: '', realEstateDisplayMap: '', realEstateMapType: '',
            hospitalityBookingSystem: '', hospitalityBookingSystemDetails: '',
            hospitalityDisplayMap: '', hospitalityMapType: '',
            blogUtilization: '', blogContentType: '',
            eventsSellTickets: '', eventsThirdParty: '', eventsThirdPartyName: '', eventsThirdPartyOther: '', eventsRequireRSVP: '',
            contentManagementNeeds: '', existingContent: '', initialContentUpload: '',
            contentUpdateFrequency: '', contentUpdateManagement: ''
          })) {
            acc[key] = currentQuestionnaire.formData[key];
          }
          return acc;
        }, {} as any)
      };
      
      // console.log('Updated form data:', updatedFormData);
      setFormData(updatedFormData);
      
      // Get the step from URL query parameters
      const stepFromQuery = getStepFromQuery();
      // console.log('Step from query:', stepFromQuery, 'Current step:', currentQuestionnaire.currentStep);
      
      // Calculate active steps based on the loaded form data
      const activeSteps = getActiveSteps(updatedFormData, activeTemplate);
      // console.log('Active steps after loading questionnaire:', activeSteps.map(step => step.title));
      
      // Make sure the step is valid for the current active steps
      const maxValidStep = Math.max(0, activeSteps.length - 1);
      const validStepFromQuery = Math.min(stepFromQuery, maxValidStep);
      
      // For completed questionnaires, always respect the URL step parameter
      // For in-progress questionnaires, don't allow steps beyond the current progress
      // Only update the current step if it's different to avoid infinite loops
      const newStep = currentQuestionnaire.isComplete || validStepFromQuery <= currentQuestionnaire.currentStep
        ? validStepFromQuery
        : Math.min(currentQuestionnaire.currentStep, maxValidStep);
        
      if (currentStep !== newStep) {
        // console.log('Setting current step to:', newStep);
        setCurrentStep(newStep);
        
        // Update URL if the step from query is invalid
        if (stepFromQuery !== validStepFromQuery) {
          const newUrl = `/questionnaire/form/${id}?step=${validStepFromQuery}`;
          // console.log('Updating URL to reflect valid step:', newUrl);
          navigate(newUrl, { replace: true });
        }
      }
    }
  }, [currentQuestionnaire, location.search, navigate, id]);

  // Load active template when component mounts - always force fresh
  useEffect(() => {
    if (currentUser) {
      // Always load a fresh template to avoid caching issues
      loadActiveTemplate(true);
    }
  }, [currentUser, loadActiveTemplate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    
    const file = e.target.files[0];
    const reader = new FileReader();
    
    reader.onloadend = () => {
      // Store the file as a base64 string
      const base64String = reader.result as string;
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: base64String
      }));
    };
    
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'radio') {
      // Use the main handleRadioChange function for consistency
      handleRadioChange(name, value);
    } else {
      setFormData((prev) => {
        // Special handling for select fields
        if (name === 'eventsThirdPartyName') {
          // If the value is not 'other', clear the eventsThirdPartyOther field
          if (value !== 'other') {
            return {
              ...prev,
              [name]: value,
              eventsThirdPartyOther: ''
            };
          }
        }
        
        // Default case
        return {
          ...prev,
          [name]: value,
        };
      });
    }
  };

  // Add new handlers for shadcn/ui components
  const handleCheckboxChange = (checked: boolean, value: string, fieldName?: string) => {
    setFormData((prev) => {
      // If fieldName is not provided, try to determine it from the current step
      let targetFieldName = fieldName;
      if (!targetFieldName) {
        const currentStepData = getActiveSteps(prev, activeTemplate)[currentStep];
        // Find the checkbox field in the current step
        const checkboxField = currentStepData.fields.find(field => field.type === 'checkbox');
        if (checkboxField) {
          targetFieldName = checkboxField.name;
        } else {
          console.warn('No checkbox field found in current step');
          return prev;
        }
      }
      
      // Get the current array for this field (or initialize as empty array)
      const currentArray = [...(prev[targetFieldName] || [])];
      
      // Update the array based on checked state
      if (checked) {
        // Add value if not already present
        if (!currentArray.includes(value)) {
          currentArray.push(value);
        }
      } else {
        // Remove value if present
        const index = currentArray.indexOf(value);
        if (index > -1) {
          currentArray.splice(index, 1);
        }
      }
      
      // console.log(`Checkbox ${targetFieldName}: ${value} ${checked ? 'checked' : 'unchecked'}, new values:`, currentArray);
      
      // Create new form data with updated array
      const newFormData = {
        ...prev,
        [targetFieldName]: currentArray
      };
      
      // Special handling for projectCategories - recalculate active steps
      if (targetFieldName === 'projectCategories') {
        const newActiveSteps = getActiveSteps(newFormData, activeTemplate);
        
        // If current step is beyond the available steps, it will be handled in useEffect
        if (currentStep >= newActiveSteps.length) {
          // console.log('Current step is beyond available steps, will adjust to:', Math.max(0, newActiveSteps.length - 1));
        }
      }
      
      return newFormData;
    });
  };

  const handleRadioChange = (name: string, value: string) => {
    // console.log(`Radio button changed: ${name} = ${value}`);
    
    setFormData((prev) => {
      // Create a copy of the previous state
      const updated = { ...prev } as { [key: string]: any };
      
      // Handle boolean-like values
      if (value === 'true' || value === 'false') {
        // For fields that accept both boolean and string
        if (
          name === 'ecommerceTax' || 
          name === 'ecommerceSubscriptions' ||
          name === 'hospitalityBookingSystem' ||
          name === 'hospitalityDisplayMap' ||
          name === 'realEstatePropertyManagement' || 
          name === 'realEstateDisplayUpdates' ||
          name === 'realEstateLeadsToCRM' || 
          name === 'realEstateDisplayMap'
        ) {
          updated[name] = value === 'true';
        } else {
          // For other fields that expect string
          updated[name] = value;
        }
      } else {
        // For regular string values
        updated[name] = value;
        
        // Special handling for eventsSellTickets
        if (name === 'eventsSellTickets') {
          // If "No" is selected, clear the third party fields
          if (value === 'no') {
            updated.eventsThirdParty = '';
            updated.eventsThirdPartyName = '';
            updated.eventsThirdPartyOther = '';
          }
        }
        
        // Special handling for eventsThirdParty
        if (name === 'eventsThirdParty') {
          // If "No" is selected, clear the third party name and other fields
          if (value === 'no') {
            updated.eventsThirdPartyName = '';
            updated.eventsThirdPartyOther = '';
          }
        }
      }
      
      // console.log('Updated form data after radio change:', updated);
      return updated as FormData;
    });
  };

  // In the handleNext function, add special handling for pageStructure
  const handleNext = async () => {
    if (!isStepValid()) return;
    
    setSavingStep(true);
    try {
      // console.log('Saving form data:', formData);
      
      // Get the active steps based on current form data
      const activeSteps = getActiveSteps(formData, activeTemplate);
      
      // Special handling for pageStructure to prevent unnecessary updates
      if (currentStepData.fields.some(field => field.name === 'pageStructure')) {
        // Only update if pageStructure has actually changed or is empty
        if (!formData.pageStructure || formData.pageStructure.length === 0) {
          // console.log('Initializing pageStructure with default homepage');
          // Initialize with a default homepage if empty
          formData.pageStructure = [{
            id: uuidv4(),
            title: 'Homepage',
            description: 'Main landing page',
            sections: []
          }];
        }
      }
      
      // Save current step data
      await updateQuestionnaireData(formData, currentStep + 1);
      
      // Move to next step
      if (currentStep < activeSteps.length - 1) {
        const nextStep = currentStep + 1;
        
        // Only update URL if the current step is different
        if (nextStep !== currentStep) {
          setCurrentStep(nextStep);
          
          // Update URL with the new step
          const currentUrl = new URL(window.location.href);
          const currentStepParam = currentUrl.searchParams.get('step');
          
          // Only update URL if the step param is different to avoid unnecessary history entries
          if (currentStepParam !== nextStep.toString()) {
            navigate(`/questionnaire/form/${id}?step=${nextStep}`);
          }
          
          window.scrollTo(0, 0);
        }
      } else {
        // For already completed questionnaires, save data and trigger email notification
        if (currentQuestionnaire?.isComplete) {
          // Update the data and trigger email notification for edits
          // console.log('Updating final step data for completed questionnaire:', formData);
          await updateQuestionnaireData(formData);
          await handleSubmit(); // This will trigger email notification for edits
          navigate('/questionnaire/success');
        } else {
          // Submit the form if it's the last step and not already completed
          // console.log('Submitting questionnaire with data:', formData);
          await handleSubmit();
        }
      }
    } catch (error) {
      console.error('Error saving step data:', error);
    } finally {
      setSavingStep(false);
    }
  };

  // Check if the form is empty (no data entered)
  const isFormEmpty = (): boolean => {
    // If we're on the first step, check if any required fields have been filled
    if (currentStep === 0) {
      const stepFields = allSteps[0].fields;
      for (const field of stepFields) {
        const value = formData[field.name];
        if (value && typeof value === 'string' && value.trim() !== '') {
          return false; // Found a non-empty field
        }
        if (Array.isArray(value) && value.length > 0) {
          return false; // Found a non-empty array field
        }
      }
      return true; // All fields are empty
    }
    return false; // Not on first step, so form is not considered empty
  };

  const handleBack = async () => {
    // If we're on the first step and the form is empty, delete the questionnaire instead of saving
    if (currentStep === 0 && isFormEmpty() && id) {
      // console.log('Form is empty, deleting questionnaire instead of saving');
      try {
        await deleteQuestionnaireDoc(currentUser?.uid || '', id);
        navigate('/questionnaire');
        return;
      } catch (error) {
        console.error('Error deleting empty questionnaire:', error);
      }
    }
    
    // Otherwise, save current data before going back
    setSavingStep(true);
    try {
      await updateQuestionnaireData(formData, currentStep - 1);
      
      if (currentStep > 0) {
        const prevStep = currentStep - 1;
        
        // Only update URL if the current step is different
        if (prevStep !== currentStep) {
          setCurrentStep(prevStep);
          
          // Update URL with the new step
          const currentUrl = new URL(window.location.href);
          const currentStepParam = currentUrl.searchParams.get('step');
          
          // Only update URL if the step param is different to avoid unnecessary history entries
          if (currentStepParam !== prevStep.toString()) {
            navigate(`/questionnaire/form/${id}?step=${prevStep}`);
          }
          
          window.scrollTo(0, 0);
        }
      } else {
        // Reload user questionnaires before navigating back to dashboard
        await loadUserQuestionnaires();
        navigate('/questionnaire');
      }
    } catch (error) {
      console.error('Error saving data before going back:', error);
    } finally {
      setSavingStep(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // First, make sure the current form data is saved
      // console.log('Saving final form data before submission:', formData);
      await updateQuestionnaireData(formData);
      
      // Then submit the questionnaire
      await submitQuestionnaire();
      
      // Reload user questionnaires before navigating to success page
      await loadUserQuestionnaires();
      
      navigate('/questionnaire/success');
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Get the current step data from the active steps
  const activeSteps = getActiveSteps(formData, activeTemplate);
  const currentStepData = activeSteps[currentStep];
  const isLastStep = currentStep === activeSteps.length - 1;

  // console.log('Current step:', currentStep, 'Current step data:', currentStepData?.title || 'undefined');

  const isStepValid = () => {
    // If currentStepData is undefined, return false
    if (!currentStepData) return false;
    
    // Add debug logging
    // console.log('Validating step:', currentStepData.title);
    // console.log('Fields to validate:', currentStepData.fields.map(f => f.name));
    
    return currentStepData.fields.every((field) => {
      if (!field.required) return true;
      
      const value = formData[field.name];
      // console.log(`Validating field: ${field.name}, value:`, value);
      
      // Check if field is conditional and if the condition is met
      if (field.conditional && !field.conditional(formData)) {
        // console.log(`Field ${field.name} is conditional and condition is not met, skipping validation`);
        return true;
      }
      
      // Handle different field types
      if (field.type === 'checkbox') {
        // For checkbox fields, check if at least one option is selected
        return Array.isArray(value) && value.length > 0;
      } else if (field.type === 'radio') {
        // For radio fields, check if a value is selected
        // For boolean fields, check both boolean and string representations
        if (field.name === 'ecommerceTax' || field.name === 'ecommerceSubscriptions' ||
            field.name === 'hospitalityBookingSystem' || field.name === 'hospitalityDisplayMap' ||
            field.name === 'realEstatePropertyManagement' || field.name === 'realEstateDisplayUpdates' ||
            field.name === 'realEstateLeadsToCRM' || field.name === 'realEstateDisplayMap' ||
            field.name === 'realEstateMapType' || field.name === 'hospitalityMapType' ||
            field.name === 'eventsSellTickets' || field.name === 'eventsThirdParty' || 
            field.name === 'eventsRequireRSVP') {
          return value === true || value === false || value === 'true' || value === 'false' || value === 'yes' || value === 'no';
        }
        return !!value;
      } else if (field.type === 'select') {
        // For select fields, check if a value is selected
        if (field.name === 'eventsThirdPartyName') {
          // Special handling for eventsThirdPartyName - only required if eventsThirdParty is 'yes'
          if (formData.eventsThirdParty === 'yes') {
            // If 'other' is selected, also check that eventsThirdPartyOther is filled
            if (value === 'other') {
              return !!value && !!formData.eventsThirdPartyOther;
            }
            return !!value;
          }
          return true;
        }
        // For select fields, check if a non-empty value is selected
        return !!value && value !== '';
      } else if (field.type === 'pageStructure') {
        // For pageStructure, consider it valid if there's at least one page
        // This prevents endless loop by not requiring sections or content
        return Array.isArray(value) && value.length > 0;
      } else if (field.type === 'repeater') {
        // For repeater fields, consider it valid if there's at least one item
        return Array.isArray(value) && value.length > 0;
      } else if (field.type === 'range') {
        // For range fields, consider it valid if both min and max values are set
        return value && typeof value === 'object' && 
               typeof value.min === 'number' && 
               typeof value.max === 'number';
      } else if (field.type === 'websiteReferences') {
        // For websiteReferences, consider it valid if there's at least one reference
        return Array.isArray(value) && value.length > 0;
      } else {
        // For text fields, check if the value is not empty
        if (field.name === 'eventsThirdPartyOther') {
          // Only validate if the parent condition is met
          if (formData.eventsThirdPartyName === 'other' && formData.eventsThirdParty === 'yes' && formData.eventsSellTickets === 'yes') {
            return typeof value === 'string' && value.trim() !== '';
          }
          return true;
        }
        return typeof value === 'string' && value.trim() !== '';
      }
    });
  };

  // Calculate active steps whenever form data changes that might affect conditional steps
  useEffect(() => {
    const activeSteps = getActiveSteps(formData, activeTemplate);
    // console.log('Recalculating active steps, current step:', currentStep, 'active steps length:', activeSteps.length);
    
    // Log the titles of all active steps
    // console.log('Active step titles:', activeSteps.map(step => step.title));
    
    // Log specific step indices for debugging
    const blogEventsIndex = activeSteps.findIndex(step => step.title === 'Blog and Events');
    const adaComplianceIndex = activeSteps.findIndex(step => step.title === 'ADA Compliance');
    const projectDescriptionIndex = activeSteps.findIndex(step => step.title === 'Project Description');
    
    // console.log('Step indices - Blog & Events:', blogEventsIndex, 'ADA Compliance:', adaComplianceIndex, 'Project Description:', projectDescriptionIndex);
    
    // Check if Content Management is included
    const contentManagementIndex = activeSteps.findIndex(step => step.title === 'Content Management');
    // console.log('Content Management step index:', contentManagementIndex);
    
    // If current step is beyond the available steps, adjust it
    if (currentStep >= activeSteps.length) {
      const newStep = Math.max(0, activeSteps.length - 1);
      // console.log('Adjusting current step from', currentStep, 'to', newStep);
      setCurrentStep(newStep);
      
      // Also update the URL to reflect the new step
      const newUrl = `/questionnaire/form/${id}?step=${newStep}`;
      navigate(newUrl, { replace: true });
    } else {
      // Even if the step number is valid, check if the current step title has changed
      // This can happen when conditional steps are removed/added in the middle of the sequence
      const currentActiveStep = activeSteps[currentStep];
      // console.log('Current active step:', currentActiveStep?.title);
    }
    
    // Using a more stable dependency array to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, id, navigate, 
      // Use string representation of categories instead of the array itself
      formData.projectCategories ? formData.projectCategories.join(',') : '',
      formData.needsBlog,
      formData.needsEvents,
      formData.restaurantReservations,
      formData.realEstatePropertyManagement,
      formData.realEstateDisplayUpdates,
      formData.realEstateLeadsToCRM,
      formData.realEstateDisplayMap,
      formData.realEstateMapType,
      formData.hospitalityBookingSystem,
      formData.hospitalityDisplayMap,
      formData.hospitalityMapType,
      formData.blogUtilization,
      formData.blogContentType,
      formData.eventsSellTickets,
      formData.eventsThirdParty,
      formData.eventsThirdPartyName,
      formData.eventsThirdPartyOther,
      formData.eventsRequireRSVP
    ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Display error message if loading failed
  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-red-500">Error Loading Questionnaire</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{loadError}</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/questionnaire')}>
              Return to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Handle case where currentStepData is undefined
  if (!currentStepData) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-red-500">Error Loading Form</CardTitle>
          </CardHeader>
          <CardContent>
            <p>There was an error loading the form data. The step might be invalid.</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/questionnaire')}>
              Return to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center p-2 mt-2 md:p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">
              {(() => {
                const sectionInfo = getCurrentSectionInfo(currentStep, getActiveSteps(formData, activeTemplate), activeTemplate, formData);
                return `Step ${sectionInfo.currentSection} of ${sectionInfo.totalSections}`;
              })()}
            </span>
            <div className="flex gap-1">
              {/* Dynamic progress indicators based on sections */}
              {activeTemplate?.sections?.sort((a: any, b: any) => a.order - b.order).filter((section: any) => {
                // Only show sections that would be visible in sidebar
                const isSectionVisible = !section.conditionalRules || section.conditionalRules.length === 0 || 
                  evaluateConditionalRules(section.conditionalRules, formData);
                
                if (!isSectionVisible) return false;
                
                // Check if section has any visible content
                if (section.subsections && section.subsections.length > 0) {
                  const visibleSubsections = section.subsections.filter((subsection: any) => {
                    return !subsection.conditionalRules || subsection.conditionalRules.length === 0 ||
                      evaluateConditionalRules(subsection.conditionalRules, formData);
                  });
                  return visibleSubsections.length > 0;
                }
                return false;
              }).map((section: any, index: number) => {
                const sectionInfo = getCurrentSectionInfo(currentStep, getActiveSteps(formData, activeTemplate), activeTemplate, formData);
                const isCurrentSection = index + 1 === sectionInfo.currentSection;
                const isCompletedSection = index + 1 < sectionInfo.currentSection;
                const subsectionProgress = isCurrentSection ? sectionInfo.subsectionProgress : (isCompletedSection ? 1 : 0);
                
                return (
                  <div 
                    key={`progress-section-${index}`}
                    className="relative h-1 w-6 md:w-8 rounded-full bg-muted overflow-hidden"
                    title={section.title}
                  >
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${
                        isCompletedSection || (isCurrentSection && subsectionProgress === 1)
                          ? 'bg-primary' 
                          : isCurrentSection 
                            ? 'bg-primary/80'
                            : 'bg-transparent'
                      }`}
                      style={{
                        width: isCurrentSection ? `${subsectionProgress * 100}%` : isCompletedSection ? '100%' : '0%'
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <CardTitle className="text-xl md:text-2xl">{currentStepData.title}</CardTitle>
            {currentStepData.description && (
              <CardDescription className="text-sm md:text-base">{currentStepData.description}</CardDescription>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="space-y-4">
              {currentStepData.fields.map((field) => (
                <div key={field.name} className="space-y-2">
                  {/* Skip rendering if the field has a conditional function that returns false */}
                  {field.conditional && !field.conditional(formData) ? null : (
                    <>
                      <label 
                        htmlFor={field.name} 
                        className="block text-sm font-medium"
                      >
                        {field.label} {field.required && <span className="text-destructive">*</span>}
                      </label>
                      
                      {(() => {
                        // Handle different field types
                        if (field.type === 'textarea') {
                          return (
                            <>
                              <textarea
                                id={field.name}
                                name={field.name}
                                placeholder={(field as TextareaField).placeholder}
                                value={formData[field.name] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                                className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              />
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'text' || field.type === 'email' || field.type === 'tel' || field.type === 'url' || field.type === 'date') {
                          return (
                            <>
                              <Input
                                id={field.name}
                                name={field.name}
                                type={field.type}
                                placeholder={(field as TextField).placeholder}
                                value={formData[field.name] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                              />
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'select') {
                          const selectField = field as SelectField;
                          const hasEmptyOption = selectField.options.some(option => option.value === '');
                          const currentValue = formData[field.name] || '';
                          
                          return (
                            <>
                              <select
                                id={field.name}
                                name={field.name}
                                value={currentValue}
                                onChange={handleInputChange}
                                required={field.required}
                                className="w-full p-2 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              >
                                {/* Add placeholder option if field is required and no empty option exists and no value is set */}
                                {field.required && !hasEmptyOption && !currentValue && (
                                  <option value="" disabled>
                                    Select {field.label.toLowerCase()}...
                                  </option>
                                )}
                                {selectField.options.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'radio') {
                          // Debug the current value of the field
                          // Special handling for advancedADACompliance to ensure default 'no' value is applied
                          let currentValue;
                          if (field.name === 'advancedADACompliance') {
                            currentValue = formData[field.name] || 'no';
                            // console.log('ADA compliance field value:', formData[field.name], 'using value:', currentValue);
                          } else if (field.name === 'ecommerceTax' || field.name === 'ecommerceSubscriptions' ||
                            field.name === 'hospitalityBookingSystem' || field.name === 'hospitalityDisplayMap' ||
                            field.name === 'realEstatePropertyManagement' || field.name === 'realEstateDisplayUpdates' ||
                            field.name === 'realEstateLeadsToCRM' || field.name === 'realEstateDisplayMap' ||
                            field.name === 'realEstateMapType' || field.name === 'hospitalityMapType') {
                            currentValue = formData[field.name] === true ? 'true' : 
                                          formData[field.name] === false ? 'false' : 
                                          String(formData[field.name] || '');
                          } else {
                            currentValue = String(formData[field.name] || '');
                          }
                          
                          // console.log(`Radio field ${field.name} value:`, formData[field.name], 'converted to:', currentValue);
                          
                          return (
                            <>
                              <RadioGroup 
                                value={currentValue}
                                onValueChange={(value: string) => handleRadioChange(field.name, value)}
                                className="space-y-2"
                              >
                                {(field as RadioField).options.map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option.value} id={`${field.name}-${option.value}`} />
                                    <label htmlFor={`${field.name}-${option.value}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </RadioGroup>
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'checkbox') {
                          return (
                            <>
                              <div className="space-y-2">
                                {(field as CheckboxField).options.map((option) => (
                                  <div key={option.value} className="flex items-center space-x-2">
                                    <Checkbox 
                                      id={`${field.name}-${option.value}`}
                                      checked={formData[field.name]?.includes(option.value) || false}
                                      onCheckedChange={(checked: boolean | 'indeterminate') => 
                                        handleCheckboxChange(checked === true, option.value, field.name)
                                      }
                                    />
                                    <label
                                      htmlFor={`${field.name}-${option.value}`}
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'file') {
                          return (
                            <>
                              <Input
                                id={field.name}
                                name={field.name}
                                type="file"
                                accept={(field as FileField).accept || ''}
                                onChange={handleFileChange}
                                required={field.required}
                                className="border-0 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                              />
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                              {formData[field.name] && (
                                <div className="mt-2">
                                  <p className="text-xs text-muted-foreground">File uploaded successfully</p>
                                  {formData[field.name].startsWith('data:image') && (
                                    <div className="mt-2 border rounded p-2 max-w-xs">
                                      <img 
                                        src={formData[field.name]} 
                                        alt="Sitemap preview" 
                                        className="max-w-full h-auto" 
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'pageStructure') {
                          return (
                            <>
                              <div className="overflow-x-auto">
                                <PageStructureBuilder
                                  value={formData.pageStructure || []}
                                  onChange={(pages) => {
                                    // Only update if the pages have actually changed to prevent infinite loops
                                    const currentPages = JSON.stringify(formData.pageStructure || []);
                                    const newPages = JSON.stringify(pages);
                                    
                                    if (currentPages !== newPages) {
                                      // console.log('PageStructure changed, updating form data');
                                      setFormData((prev) => ({
                                        ...prev,
                                        pageStructure: pages
                                      }));
                                    }
                                  }}
                                />
                              </div>
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'range') {
                          const rangeField = field as RangeField;
                          const isBudgetRange = field.name === 'budget';
                          
                          return (
                            <>
                              <RangeSlider
                                min={rangeField.min}
                                max={rangeField.max}
                                step={isBudgetRange ? 1000 : 1}
                                value={formData[field.name] || (isBudgetRange ? { min: 10000, max: 150000 } : null)}
                                onChange={(value) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    [field.name]: value
                                  }));
                                }}
                                showTickMarks={isBudgetRange}
                                tickStep={isBudgetRange ? 10000 : 10}
                                formatValue={isBudgetRange ? (val) => `$${val.toLocaleString()}` : undefined}
                                className="w-full"
                              />
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'repeater') {
                          return (
                            <>
                              <div className="overflow-x-auto">
                                <PageStructureBuilder
                                  value={formData[field.name] || []}
                                  onChange={(pages) => {
                                    // Only update if the pages have actually changed to prevent infinite loops
                                    const currentPages = JSON.stringify(formData[field.name] || []);
                                    const newPages = JSON.stringify(pages);
                                    
                                    if (currentPages !== newPages) {
                                      // console.log('Repeater field changed, updating form data');
                                      setFormData((prev) => ({
                                        ...prev,
                                        [field.name]: pages
                                      }));
                                    }
                                  }}
                                />
                              </div>
                              {field.helpText && (
                                <p 
                                  className="text-xs text-muted-foreground mt-2"
                                  dangerouslySetInnerHTML={{ __html: field.helpText }}
                                />
                              )}
                            </>
                          );
                        }
                        
                        if (field.type === 'websiteReferences') {
                          let placeholder = "Briefly describe what you like about this website";
                          let buttonText = "Add Website";
                          
                          // Set different placeholder and button text based on field name
                          if (field.name === 'competitorWebsites') {
                            placeholder = "Briefly describe this competitor's website";
                            buttonText = "Add Competitor Website";
                          } else if (field.name === 'inspirationalWebsites') {
                            placeholder = "Briefly describe what you like about this website";
                            buttonText = "Add Inspirational Website";
                          } else if (field.name === 'dislikedWebsites') {
                            placeholder = "Briefly describe what you dislike about this website";
                            buttonText = "Add Website You Dislike";
                          }
                          
                          // Add margin-bottom to all except the last field (dislikedWebsites)
                          const isLastField = field.name === 'dislikedWebsites';
                          const containerClass = isLastField ? '' : 'mb-8';
                          
                          return (
                            <>
                              <div className={containerClass}>
                                <div className="overflow-x-auto">
                                  <WebsiteReferenceBuilder
                                    value={formData[field.name] || []}
                                    onChange={(refs) => {
                                      // Only update if the refs have actually changed to prevent infinite loops
                                      const currentRefs = JSON.stringify(formData[field.name] || []);
                                      const newRefs = JSON.stringify(refs);
                                      
                                      if (currentRefs !== newRefs) {
                                        // console.log(`${field.name} changed, updating form data`);
                                        setFormData((prev) => ({
                                          ...prev,
                                          [field.name]: refs
                                        }));
                                      }
                                    }}
                                    placeholder={placeholder}
                                    buttonText={buttonText}
                                  />
                                </div>
                                {field.helpText && (
                                  <p 
                                    className="text-xs text-muted-foreground mt-1"
                                    dangerouslySetInnerHTML={{ __html: field.helpText }}
                                  />
                                )}
                              </div>
                            </>
                          );
                        }
                        
                        // Default case
                        return null;
                      })()}
                    </>
                  )}
                </div>
              ))}
            </div>
          </form>
        </CardContent>
        <CardFooter className="flex justify-between flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={handleBack}
            disabled={savingStep || submitting}
            className="w-full sm:w-auto"
          >
            {currentStep === 0 ? 'Cancel' : 'Previous'}
          </Button>
          <Button 
            onClick={handleNext}
            disabled={!isStepValid() || savingStep || submitting}
            className="w-full sm:w-auto"
          >
            {savingStep ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-r-transparent"></span>
                Saving...
              </>
            ) : isLastStep ? (
              submitting ? 'Submitting...' : 'Submit'
            ) : (
              'Next'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default QuestionnaireForm; 